import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { rhm, apiError, money } from "../api";
import { Badge, Btn, Card, Empty, Field, Input, Select } from "../ui";

const TYPES = [{ value: "percentage", label: "% off the combined price" },
    { value: "amount", label: "₹ off the combined price" }];

/** Bundles: pick 2+ products, set one saving, and the cart applies it automatically. */
export const BundlesManager = ({ editable }) => {
    const qc = useQueryClient();
    const [draft, setDraft] = useState(null);

    const { data: bundles = [] } = useQuery({
        queryKey: ["rhm-bundles"], queryFn: async () => (await rhm.get("/bundles")).data,
    });
    const { data: products = [] } = useQuery({
        queryKey: ["rhm-products"], queryFn: async () => (await rhm.get("/products")).data,
    });

    const refresh = () => qc.invalidateQueries({ queryKey: ["rhm-bundles"] });
    const create = useMutation({
        mutationFn: async (body) => (await rhm.post("/bundles", body)).data,
        onSuccess: () => { refresh(); setDraft(null); toast.success("Bundle created"); },
        onError: (e) => toast.error(apiError(e)),
    });
    const patch = useMutation({
        mutationFn: async ({ id, body }) => (await rhm.patch(`/bundles/${id}`, body)).data,
        onSuccess: () => { refresh(); toast.success("Bundle saved"); },
        onError: (e) => toast.error(apiError(e)),
    });
    const remove = useMutation({
        mutationFn: async (id) => (await rhm.delete(`/bundles/${id}`)).data,
        onSuccess: () => { refresh(); toast.success("Bundle deleted"); },
        onError: (e) => toast.error(apiError(e)),
    });

    const blank = { name: "", product_ids: [], discount_type: "percentage", value: 10, active: true, note: "" };
    const editing = draft || blank;
    const toggleProduct = (id) => setDraft({
        ...editing,
        product_ids: editing.product_ids.includes(id)
            ? editing.product_ids.filter((x) => x !== id)
            : [...editing.product_ids, id],
    });

    return (
        <Card title="Bundles (A + B + C at one price)" data-testid="bundles-manager"
            sub="Add every product of a bundle to the cart and the saving is applied automatically at checkout.">
            <div className="border border-white/10 p-4">
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Bundle name">
                        <Input value={editing.name} disabled={!editable} data-testid="bundle-name"
                            placeholder="The complete routine"
                            onChange={(e) => setDraft({ ...editing, name: e.target.value })} />
                    </Field>
                    <Field label="Saving type">
                        <Select value={editing.discount_type} disabled={!editable} data-testid="bundle-type"
                            onChange={(e) => setDraft({ ...editing, discount_type: e.target.value })} options={TYPES} />
                    </Field>
                    <Field label={editing.discount_type === "percentage" ? "Percent off" : "Amount off (₹)"}>
                        <Input type="number" min="0" value={editing.value} disabled={!editable} data-testid="bundle-value"
                            onChange={(e) => setDraft({ ...editing, value: Number(e.target.value) })} />
                    </Field>
                    <Field label="Note shown on the website" className="md:col-span-3">
                        <Input value={editing.note} disabled={!editable} data-testid="bundle-note"
                            onChange={(e) => setDraft({ ...editing, note: e.target.value })} />
                    </Field>
                </div>
                <div className="mt-4">
                    <div className="mono text-[10px] tracking-[0.2em] text-gold mb-2">PRODUCTS IN THIS BUNDLE</div>
                    <div className="flex flex-wrap gap-2">
                        {products.map((p) => (
                            <button key={p.id} type="button" disabled={!editable}
                                data-testid={`bundle-pick-${p.id}`} onClick={() => toggleProduct(p.id)}
                                className={`px-3 py-2 border mono text-[10px] tracking-[0.14em] transition-colors ${
                                    editing.product_ids.includes(p.id)
                                        ? "border-gold text-gold bg-gold/10" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                                {p.product_code} · {p.name}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                    <Btn disabled={!editable || editing.product_ids.length < 2 || !editing.name.trim()}
                        data-testid="bundle-create" onClick={() => create.mutate(editing)}>
                        <Plus className="w-3 h-3 mr-1 inline" /> CREATE BUNDLE
                    </Btn>
                    {draft && <Btn variant="ghost" onClick={() => setDraft(null)}>CLEAR</Btn>}
                </div>
            </div>

            <div className="mt-5 space-y-2">
                {!bundles.length && <Empty msg="No bundles yet. Pick two or more products above." />}
                {bundles.map((b) => (
                    <div key={b.id} className="border border-white/10 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
                        data-testid={`bundle-row-${b.id}`}>
                        <div>
                            <div className="text-sm">{b.name}</div>
                            <div className="mono text-[9px] tracking-[0.16em] text-zinc-500 mt-0.5">
                                {(b.items || []).map((i) => i.name).join(" + ")}
                            </div>
                            <div className="mono text-[9px] tracking-[0.16em] mt-1 text-zinc-400">
                                {money(b.list_price)} → <span className="text-gold">{money(b.bundle_price)}</span>
                                {" "}· SAVE {money(b.discount)}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge tone={b.active ? "success" : "muted"}>{b.active ? "Live" : "Off"}</Badge>
                            <Btn variant="ghost" disabled={!editable} data-testid={`bundle-toggle-${b.id}`}
                                onClick={() => patch.mutate({ id: b.id, body: { active: !b.active } })}>
                                {b.active ? "TURN OFF" : "TURN ON"}
                            </Btn>
                            <Btn variant="ghost" disabled={!editable} data-testid={`bundle-delete-${b.id}`}
                                onClick={() => window.confirm(`Delete the bundle "${b.name}"?`) && remove.mutate(b.id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </Btn>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
};

export default BundlesManager;
